/**
 * PremiumDialogHeader
 * -----------------------------------------------------------------------------
 * Cabeçalho padrão dos modais do sistema (padrão "Nova anotação" —
 * ContactNoteEditorDialog): faixa suave com gradiente, ícone em quadrado
 * arredondado, título compacto e descrição em texto pequeno.
 *
 * Uso recomendado junto de:
 *   <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
 *     <PremiumDialogHeader icon={Tag} title="Nova Categoria" description="..." />
 *     <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4"> ... </div>
 *     <DialogFooter className="border-t bg-muted/30 px-6 py-3"> ... </DialogFooter>
 *   </DialogContent>
 *
 * @version 1.0.0
 * RELEASE: 2026-08-21 v1.0.0 - Criação inicial para padronizar os modais de
 *          categoria, fase e classificação de tarefas.
 */
import type { LucideIcon } from 'lucide-react';
import { DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface PremiumDialogHeaderProps {
  /** Ícone exibido no quadrado arredondado à esquerda do título */
  icon: LucideIcon;
  /** Título do modal */
  title: string;
  /** Descrição curta (opcional) */
  description?: string;
  /** Classes extras para a faixa do cabeçalho */
  className?: string;
}

export function PremiumDialogHeader({
  icon: Icon,
  title,
  description,
  className,
}: PremiumDialogHeaderProps) {
  return (
    <div
      className={cn(
        'px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent',
        className,
      )}
    >
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-base">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
            <Icon className="h-3.5 w-3.5 text-primary" />
          </div>
          {title}
        </DialogTitle>
        {description && (
          <DialogDescription className="text-xs text-muted-foreground">
            {description}
          </DialogDescription>
        )}
      </DialogHeader>
    </div>
  );
}
